/**
 * Invoices Feature Constants
 * All hard-coded values, configuration options, and enums for the invoices feature
 */

// ============================================================================
// INVOICE STATUS OPTIONS
// ============================================================================

/**
 * Invoice status options for filtering and display
 * Use translation keys for labels: t(`status.${value}`)
 */
export const INVOICE_STATUS_OPTIONS = [
  { value: "pending", label: "status.pending" },
  { value: "paid", label: "status.paid" },
];

/**
 * Status colors for invoice badges
 */
export const INVOICE_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  paid: "bg-green-100 text-green-800 border-green-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-gray-100 text-gray-800 border-gray-200",
  delivered: "bg-green-100 text-green-800 border-green-200",
};

/**
 * Invoice status values (constants)
 */
export const INVOICE_STATUS_VALUES = {
  PENDING: "pending",
  PAID: "paid",
  PROCESSING: "processing",
  CANCELLED: "cancelled",
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================

/**
 * Invoice filter configuration for BaseFilter component
 */
export const INVOICE_FILTER_CONFIG = {
  showPhoneFilter: false,
  showCityFilter: false,
  showUserFilter: true,
  showDateRangeFilter: true,
  phoneFieldName: "customer.phone_q",
  cityFieldName: "customer.city",
  userFieldName: "client",
  phonePlaceholder: "0600000000",
  entityName: "invoices",
  maxTabCols: 3,
};

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Invoice form field names
 */
export const INVOICE_FORM_FIELDS = {
  CLIENT: "client",
  PACKAGES: "packages",
  AMOUNT: "amount",
  DUE_DATE: "dueDate",
  STATUS: "status",
  NOTES: "notes",
  PAYMENT_METHOD: "paymentMethod",
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for invoice operations
 */
export const INVOICE_TOAST_KEYS = {
  GENERATING_INVOICE: "invoices.generatingInvoice",
  INVOICE_GENERATED: "invoices.invoiceGenerated",
  INVOICE_UPDATED: "invoices.invoiceUpdated",
  INVOICE_DELETED: "invoices.invoiceDeleted",
  PAYMENT_RECORDED: "invoices.paymentRecorded",
  SELECT_PACKAGES_ERROR: "invoices.selectPackagesError",
  ERROR_OCCURRED: "common.errorOccurred",
  ERROR_LOADING_DATA: "common.errorLoadingData",
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Invoice-related route paths
 */
export const INVOICE_ROUTES = {
  LIST: "/invoices",
  CREATE: "/invoices/create",
  DETAILS: "/invoices/:id",
  EDIT: "/invoices/edit/:id",
};

// ============================================================================
// EMPTY STATES
// ============================================================================

/**
 * Empty state messages (translation keys)
 */
export const INVOICE_EMPTY_STATES = {
  NO_CLIENTS_AVAILABLE: "invoices.noClientsAvailable",
  NO_CLIENT_SELECTED: "invoices.noClientSelected",
  NO_PACKAGES_AVAILABLE: "invoices.noPackagesAvailable",
  SELECT_CLIENT_PROMPT: "invoices.selectClientPrompt",
  ALL_PACKAGES_INVOICED: "invoices.allPackagesInvoiced",
};

// ============================================================================
// UI TEXT KEYS
// ============================================================================

/**
 * UI text translation keys
 */
export const INVOICE_UI_TEXT = {
  CREATE_INVOICE: "invoices.createInvoice",
  GENERATE_INVOICE: "invoices.generateInvoice",
  SELECT_USER: "invoices.selectUser",
  INVOICE_SUMMARY: "invoices.invoiceSummary",
  TOTAL_AMOUNT: "invoices.totalAmount",
  SELECT_ALL: "invoices.selectAll",
  DESELECT_ALL: "invoices.deselectAll",
  PACKAGES_SELECTED: "invoices.packagesSelected",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for invoice-related queries
 */
export const INVOICE_QUERY_KEYS = {
  BASE: "invoices",
  DETAIL: "invoice",
  PACKAGES_FOR_INVOICE: "packagesInvoices",
  CLIENT_INVOICES: "client-invoices",
};

// ============================================================================
// PDF EXPORT CONFIGURATION
// ============================================================================

/**
 * PDF export configuration for invoices
 */
export const INVOICE_PDF_CONFIG = {
  FILE_NAME_PREFIX: "invoice",
  DATE_FORMAT: "yyyy-MM-dd",
  PAGE_SIZE: "A4",
  ORIENTATION: "portrait",
};

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Invoice list pagination settings
 */
export const INVOICE_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};
